import { type FC, useEffect, useState } from "react";
import { FiCheck, FiCopy, FiMail } from "react-icons/fi";
import { LinkButton } from "./LinkButton";
import { type MailDraft, toMailto } from "./mailDraft";

/**
 * お客様にメールを送ってもらう画面の共通部品。宛先・件名・本文が入った状態でメールソフトを開くボタンと、
 * 開かない場合に書き写せるよう同じ内容のプレビュー (項目ごとにコピーできる) を並べる。
 *
 * ボタンとプレビューは同じ {@link MailDraft} から作るので、画面で見た内容と開いたメールが食い違わない。
 * `buttonLabel` を渡さなければボタンを出さず、コピーして送ってもらうプレビューだけになる
 * (案内の文は呼び出し側の画面が持つ)。
 */
export const MailDraftComposer: FC<{ draft: MailDraft; buttonLabel?: string }> = ({
  draft,
  buttonLabel,
}) => {
  const preview = (
    <dl className="divide-y divide-neutral-200 rounded border border-neutral-200 text-sm">
      <DraftRow label="宛先" value={draft.to} />
      <DraftRow label="件名" value={draft.subject} emptyHint="件名にご用件をご記入ください" />
      <DraftRow label="本文" value={draft.body} multiline />
    </dl>
  );
  if (!buttonLabel) return preview;

  return (
    <div className="space-y-5">
      <div>
        <LinkButton href={toMailto(draft)} className="gap-2">
          <FiMail size={14} />
          {buttonLabel}
        </LinkButton>
        <p className="mt-2 text-neutral-500 text-xs">
          下記の内容を入力した状態で、メールソフトが開きます。
        </p>
      </div>

      <div>
        <p className="mb-2 text-neutral-600 text-xs">
          メールソフトが開かない場合は、下記をコピーしてお送りください。
        </p>
        {preview}
      </div>
    </div>
  );
};

const COPIED_MS = 2000;

const DraftRow: FC<{
  label: string;
  value: string;
  multiline?: boolean;
  /** 値が空のときに出す案内 (お客様に書いてもらう項目)。空ならコピーのボタンも出さない */
  emptyHint?: string;
}> = ({ label, value, multiline, emptyHint }) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), COPIED_MS);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      // コピーできない環境 (権限なし・非対応) では、表示中のテキストを選択して写してもらう
    }
  };

  if (!value) {
    return (
      <div className="flex items-start gap-3 px-3 py-2.5">
        <dt className="w-10 shrink-0 pt-0.5 text-neutral-500 text-xs">{label}</dt>
        <dd className="min-w-0 flex-1 text-neutral-400">{emptyHint}</dd>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-3 px-3 py-2.5">
      <dt className="w-10 shrink-0 pt-0.5 text-neutral-500 text-xs">{label}</dt>
      <dd
        className={`min-w-0 flex-1 break-words text-neutral-900 ${multiline ? "whitespace-pre-wrap leading-relaxed" : ""}`}
      >
        {value}
      </dd>
      <button
        type="button"
        onClick={copy}
        aria-label={copied ? `${label}をコピーしました` : `${label}をコピー`}
        title={copied ? "コピーしました" : "コピー"}
        className="shrink-0 rounded p-1 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900"
      >
        {copied ? <FiCheck size={14} /> : <FiCopy size={14} />}
      </button>
    </div>
  );
};
